import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { Search, Layers, Wallet, Eye } from "lucide-react";
import { api } from "../lib/api";
import { Input } from "../components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import CardTile from "../components/CardTile";
import ChaseHeartButton from "../components/ChaseHeartButton";
import PublicCardDialog from "../components/PublicCardDialog";
import Seo from "../components/Seo";
import { formatPrice } from "../lib/currency";
import { useAuth } from "../context/AuthContext";
import { useCurrency } from "../context/CurrencyContext";

export default function Showcase() {
  const { token } = useParams();
  const { user } = useAuth();
  const currencyCtx = useCurrency();
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  const [search, setSearch] = useState("");
  const [gameFilter, setGameFilter] = useState("all");
  const [collectionFilter, setCollectionFilter] = useState("all");
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    api.get(`/showcase/${token}`)
      .then((res) => setData(res.data))
      .catch(() => setError(true));
  }, [token]);

  const cards = useMemo(() => data?.cards || [], [data]);
  const gameTypes = useMemo(() => Array.from(new Set(cards.map((c) => c.game_type))), [cards]);
  const fmt = (usd) => formatPrice(usd, data?.currency || "USD", currencyCtx?.rates, currencyCtx?.symbols);

  const filtered = useMemo(() => {
    return cards.filter((c) => {
      if (gameFilter !== "all" && c.game_type !== gameFilter) return false;
      if (collectionFilter !== "all" && (c.collection_id || "uncategorized") !== collectionFilter) return false;
      if (search && !c.name.toLowerCase().includes(search.toLowerCase())) return false;
      return true;
    });
  }, [cards, gameFilter, collectionFilter, search]);

  const viewCard = (card) => setSelected(card);

  if (error) {
    return (
      <div className="min-h-screen bg-[#05060f] text-white flex items-center justify-center px-6">
        <div data-testid="showcase-not-found" className="text-center max-w-md">
          <Eye className="w-10 h-10 text-zinc-700 mx-auto mb-4" />
          <h1 className="font-display text-2xl font-bold mb-2">This showcase isn't available</h1>
          <p className="text-sm text-zinc-500">The link is either private, disabled by its owner, or doesn't exist.</p>
        </div>
      </div>
    );
  }

  if (!data) {
    return <div className="min-h-screen bg-[#05060f] flex items-center justify-center"><div className="w-8 h-8 border-2 border-white/20 border-t-[#A78BFA] rounded-full animate-spin" /></div>;
  }

  return (
    <div className="min-h-screen bg-[#05060f] text-white px-6 py-12">
      <Seo
        title={`${data.owner_name}'s Card Showcase — CardArk`}
        description={`Browse ${data.owner_name}'s trading card collection: ${data.stats.total_cards} cards, searchable and filterable.`}
      />
      <div className="max-w-7xl mx-auto">
        <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-[#A78BFA] border border-[#A78BFA]/30 rounded-full px-4 py-1.5 bg-[#A78BFA]/5 mb-4">
          <Eye className="w-3 h-3" /> Shared showcase · view only
        </span>
        <h1 data-testid="showcase-owner-title" className="font-display text-3xl md:text-4xl font-bold mb-1">
          {data.owner_name}'s Binder
        </h1>
        <p className="text-zinc-400 text-sm mb-8">Browse and filter the collection — click any card to see it up close.</p>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
          <div data-testid="showcase-stat-total-cards" className="border border-white/10 bg-[#111113] rounded-xl p-5">
            <Layers className="w-5 h-5 text-[#A78BFA] mb-2" />
            <p className="text-2xl font-display font-bold">{data.stats.total_cards}</p>
            <p className="text-xs text-zinc-500">Total cards</p>
          </div>
          {data.stats.total_value != null && (
            <div data-testid="showcase-stat-total-value" className="border border-white/10 bg-[#111113] rounded-xl p-5">
              <Wallet className="w-5 h-5 text-[#F59E0B] mb-2" />
              <p className="text-2xl font-display font-bold">{fmt(data.stats.total_value) ?? "$0.00"}</p>
              <p className="text-xs text-zinc-500">Estimated Market Value</p>
            </div>
          )}
        </div>

        <div className="flex flex-col md:flex-row gap-4 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
            <Input
              data-testid="showcase-search-input"
              placeholder="Search this collection..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10 bg-[#111113] border-white/10 rounded-full"
            />
          </div>
          <Select value={collectionFilter} onValueChange={setCollectionFilter}>
            <SelectTrigger data-testid="showcase-collection-select" className="w-full md:w-64 bg-[#111113] border-white/10 rounded-full shrink-0">
              <SelectValue placeholder="All collections" />
            </SelectTrigger>
            <SelectContent className="max-h-80 bg-[#111113] border-white/10 text-white">
              <SelectItem data-testid="showcase-collection-option-all" value="all">All collections</SelectItem>
              <SelectItem value="uncategorized">Uncategorized</SelectItem>
              {(data.collections || []).map((c) => (
                <SelectItem key={c.id} data-testid={`showcase-collection-option-${c.id}`} value={c.id}>{c.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex gap-2 mb-8 flex-wrap">
          <button data-testid="showcase-game-filter-all" onClick={() => setGameFilter("all")} className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${gameFilter === "all" ? "border-[#A78BFA] text-[#A78BFA] bg-[#A78BFA]/10" : "border-white/10 text-zinc-400 hover:text-white"}`}>All types</button>
          {gameTypes.map((g) => (
            <button key={g} data-testid={`showcase-game-filter-${g}`} onClick={() => setGameFilter(g)} className={`text-xs px-3 py-1.5 rounded-full border capitalize transition-colors ${gameFilter === g ? "border-[#A78BFA] text-[#A78BFA] bg-[#A78BFA]/10" : "border-white/10 text-zinc-400 hover:text-white"}`}>{g}</button>
          ))}
        </div>

        {filtered.length === 0 ? (
          <div data-testid="showcase-empty-state" className="text-center py-24 border border-dashed border-white/10 rounded-2xl text-zinc-500">
            No cards match this filter.
          </div>
        ) : (
          <div data-testid="showcase-card-grid" className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-5">
            {filtered.map((card, i) => (
              <div key={card.id} className="relative">
                <CardTile card={card} index={i} viewOnly onView={viewCard} formatPrice={fmt} />
                {user && (
                  <div className="absolute top-2 left-2 z-10">
                    <ChaseHeartButton card={card} />
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        <footer className="mt-16 pt-8 border-t border-white/10 text-center text-xs text-zinc-500">
          Shared via <a href="/" className="text-[#A78BFA] hover:underline">CardArk</a> — film your stack, know your collection.
        </footer>
      </div>
      {selected && (
        <PublicCardDialog
          card={selected}
          open={!!selected}
          onOpenChange={(o) => !o && setSelected(null)}
          fmt={fmt}
          ownerName={data.owner_name}
        />
      )}
    </div>
  );
}
